import React from "react";
import { parseISO, subDays, format } from "date-fns";
import { useParams } from "react-router-dom";

import {
  notification_color_keys,
  useNotifications,
} from "utils/hooks/useNotifications";
import {
  MAX_SESSIONS_DAYS_COUNT,
  NEW_SECTION_OPTION_VALUE,
  NONE_OPTION_SECTION_VALUE,
  MANAGE_RECORDINGS_HOOK_TEXTS,
  column_keys,
} from "../constants/ManageRecordings.constant";
import { manage_recordings_api } from "../api/ManageRecordings.api";
import { getUpdatedRecordingToSections } from "../utils/ManageRecordings.util";
import { api } from "data";
import { baseApiClient } from "data/modules/api/api.client";
import { logError } from "utils/error";
import { isRequestSuccessful } from "utils/Utils";
import { is_empty } from "features/Common/utils/common.utils";
import { schedule_types_ids } from "features/Listings/constants/Listings.constants";
import { LISTING_PARTICIPANTS_API } from "ui/pages/customers/ManageBookings/moduleAPI";
import {
  DAY_DATE_TIME_COMPLETE_FORMAT,
  DEFAULT_YEAR_MONTH_DATE_FORMAT,
} from "constants/date-fns/dateTime.constant";
import styles from "../ManageRecordings.module.css";

import { useManageOfferingsStore } from "features/ManageOfferings/store/useManageOfferingsStore";

const startDate = format(
  subDays(new Date(), MAX_SESSIONS_DAYS_COUNT),
  DEFAULT_YEAR_MONTH_DATE_FORMAT
);
const endDate = format(new Date(), DEFAULT_YEAR_MONTH_DATE_FORMAT);

const useManageRecordings = () => {
  const setCurrentOffering = useManageOfferingsStore(
    (state) => state.setCurrentOffering
  );
  const setCurrentRecordedContentDetails = useManageOfferingsStore(
    (state) => state.setCurrentRecordedContentDetails
  );

  const [showViewRecordingModal, setShowViewRecordingModal] =
    React.useState(false);
  const [viewRecordingModalData, setViewRecordingModal] = React.useState({});
  const [recordToSelectedSection, setRecordToSelectedSection] = React.useState(
    {}
  );
  const [showSectionChangesModal, setShowSectionChangesModal] =
    React.useState(false);
  const [zoomRcDetails, setZoomRcDetails] = React.useState({});
  const [rcListingUuid, setRcListingUuid] = React.useState(null);
  const [parentOffering, setParentOffering] = React.useState({});
  const [batches, setBatches] = React.useState([]);
  const [recordToSelectedBatch, setRecordToSelectedBatch] = React.useState({});
  const [sessions, setSessions] = React.useState([]);
  const [recordToSelectedSession, setRecordToSelectedSession] = React.useState(
    {}
  );
  const [customers, setCustomers] = React.useState([]);
  const [recordToSelectedCustomer, setRecordToSelectedCustomer] =
    React.useState({});
  const [isPageDataLoaing, setIsPageDataLoading] = React.useState(true);
  const [courseSectionOptions, setCourseSectionOptions] = React.useState([
    {
      id: MANAGE_RECORDINGS_HOOK_TEXTS.NONE_SECTION_OPTION_ID,
      label: MANAGE_RECORDINGS_HOOK_TEXTS.INITIAL_SECTION_OPTION_NONE_LABEL,
      value: NONE_OPTION_SECTION_VALUE,
    },
    {
      id: MANAGE_RECORDINGS_HOOK_TEXTS.NEW_SECTION_OPTION_ID,
      label: (
        <span className={styles.create_new_section_label}>
          {MANAGE_RECORDINGS_HOOK_TEXTS.CREATE_NEW_SECTION_LABEL}
        </span>
      ),
      value: NEW_SECTION_OPTION_VALUE,
    },
  ]);

  const { listing_uuid: listingUuid } = useParams();
  const { notify } = useNotifications();

  const currentOffering = useManageOfferingsStore(
    (state) => state.currentOffering
  );

  const effectiveOfferingType = !is_empty(parentOffering?.type)
    ? parentOffering?.type
    : currentOffering?.type;

  const showBatchColumn = [
    schedule_types_ids.group_class,
    schedule_types_ids.one_on_one_class,
  ].includes(effectiveOfferingType);

  const showSessionColumn = [
    schedule_types_ids.rolling_class,
    schedule_types_ids.webinar,
  ].includes(effectiveOfferingType);

  const showCustomerColumn = [
    schedule_types_ids.one_on_one_appointment,
  ].includes(effectiveOfferingType);

  const handleToggleShowViewRecordingModal = () => {
    setShowViewRecordingModal((prev) => !prev);
  };

  const toggleShowSaveSectionChangesModal = () =>
    setShowSectionChangesModal((prev) => !prev);

  const getZoomRcOfferingDetails = async () => {
    try {
      let rcListingUuid = listingUuid;

      // 1. Fetch unmapped recordings list first to extract rc_listing_uuid
      const unmappedRecordingsResponse = await baseApiClient.get(
        manage_recordings_api.get_recordings,
        {
          params: {
            origin_listing_uuid: listingUuid,
          },
        }
      );

      if (isRequestSuccessful(unmappedRecordingsResponse?.status)) {
        const recordingsData = unmappedRecordingsResponse?.data?.data;
        const unmappedList = recordingsData?.recordings || recordingsData || [];
        const foundRcUuid =
          recordingsData?.rc_listing_uuid ||
          (Array.isArray(unmappedList) && unmappedList[0]?.rc_listing_uuid);

        if (foundRcUuid) {
          rcListingUuid = foundRcUuid;
        }
      }

      setRcListingUuid(rcListingUuid);

      // 2. Fetch recorded content details using rc_listing_uuid
      const zoomDetailsResponse = await baseApiClient.get(
        `${api.get_recordedcontent_details}${listingUuid}`,
        {
          params: {
            rc_listing_uuid: rcListingUuid,
          },
        }
      );
      if (isRequestSuccessful(zoomDetailsResponse?.status)) {
        const { event } = zoomDetailsResponse?.data?.data || {};
        setZoomRcDetails(event);
        setCurrentRecordedContentDetails(event);
        const { categories, parent_offering: mainOfferingData } = event || {};
        let offeringToUse = mainOfferingData;

        if (is_empty(offeringToUse)) {
          const directListingResponse = await baseApiClient.get(
            `${api.get_listing_details}/${listingUuid}`
          );
          if (isRequestSuccessful(directListingResponse?.status)) {
            const fetchedEvent = directListingResponse?.data?.data?.event;
            if (!is_empty(fetchedEvent)) {
              offeringToUse = fetchedEvent;
            }
          }
        }

        if (!is_empty(offeringToUse)) {
          setParentOffering(offeringToUse);
          setCurrentOffering(offeringToUse);
        }

        if (!is_empty(categories)) {
          const availableCourseSections = categories.map((sectionData) => ({
            ...sectionData,
            label: sectionData.title,
            value: sectionData.uuid,
            id: sectionData.uuid,
          }));
          setCourseSectionOptions((prev) => {
            let newPrev = [...prev];
            newPrev.splice(prev.length - 1, 0, ...availableCourseSections);
            return newPrev;
          });
        }

        const effectiveOfferingData = offeringToUse || currentOffering;

        switch (effectiveOfferingData?.type) {
          case schedule_types_ids.one_on_one_class:
          case schedule_types_ids.group_class: {
            const classesDetailsResponse = await baseApiClient.get(
              `${api.get_listing_details}/${effectiveOfferingData?.uuid}`
            );
            if (isRequestSuccessful(classesDetailsResponse?.status)) {
              const classesData = classesDetailsResponse?.data?.data;
              if (!is_empty(classesData?.event?.classes)) {
                const { classes } = classesData.event;
                const availableBatches = classes?.map((batch) => ({
                  ...batch,
                  id: batch.uuid,
                  label: batch.timings,
                  value: batch.uuid,
                }));
                setBatches([...availableBatches]);
              }
            }
            break;
          }

          // For both rolling class or webinar type
          case schedule_types_ids.rolling_class:
          case schedule_types_ids.webinar: {
            const sessionDetailsResponse = await baseApiClient.get(
              api.dashboard.sessions_list,
              {
                params: {
                  listing_uuid: effectiveOfferingData?.uuid,
                  start_date: startDate,
                  end_date: endDate,
                  is_booked: true,
                },
              }
            );
            if (isRequestSuccessful(sessionDetailsResponse?.status)) {
              const sessionData = sessionDetailsResponse?.data?.data;
              const { sessions: sessionsList } = sessionData || {};
              const availableSessions = sessionsList?.map((session) => ({
                ...session,
                id: session.slot_uid,
                label: format(
                  parseISO(session.start_datetime),
                  DAY_DATE_TIME_COMPLETE_FORMAT
                ),
                value: session.slot_uid,
              }));
              setSessions([...availableSessions]);
            }
            break;
          }

          case schedule_types_ids.one_on_one_appointment: {
            const mainListingUuid = effectiveOfferingData?.uuid;
            if (!mainListingUuid) {
              notify(
                MANAGE_RECORDINGS_HOOK_TEXTS.NO_OFFERING_SELECTED,
                notification_color_keys.error
              );
              return;
            }
            const customerDetailsResponse = await baseApiClient.get(
              LISTING_PARTICIPANTS_API,
              {
                params: {
                  listing_uuid: mainListingUuid,
                  unique_customer: true,
                  page: MANAGE_RECORDINGS_HOOK_TEXTS.PARTICIPANTS_PAGE_ALL,
                },
              }
            );
            if (isRequestSuccessful(customerDetailsResponse?.status)) {
              const customerData = customerDetailsResponse?.data?.data;
              if (!is_empty(customerData?.participants)) {
                const { participants } = customerData;
                const availableCstomers = participants?.map((participant) => ({
                  ...participant,
                  id: participant.username,
                  label: participant.customer_name,
                  value: participant.username,
                }));
                setCustomers([...availableCstomers]);
              }
            }
            break;
          }
        }
      }
      setIsPageDataLoading(false);
    } catch (error) {
      setIsPageDataLoading(false);
      logError({
        error,
        occuredAt: MANAGE_RECORDINGS_HOOK_TEXTS.CUSTOM_REQ_OCCURED_AT,
        when: MANAGE_RECORDINGS_HOOK_TEXTS.CUSTOM_REQ_WHEN,
      });
    }
  };

  const handleSaveChnagesClick = () => {
    const updatedRecordingToSections = getUpdatedRecordingToSections(
      recordToSelectedSection
    );
    const recordingSectionUuids = Object.keys(updatedRecordingToSections);
    let errorMessage = "";
    if (!is_empty(recordingSectionUuids)) {
      if (
        showBatchColumn &&
        recordingSectionUuids.some(
          (recordingUuid) => !recordToSelectedBatch?.[recordingUuid]
        )
      ) {
        errorMessage = MANAGE_RECORDINGS_HOOK_TEXTS.SELECT_BATCH_ERROR;
      }

      if (
        showSessionColumn &&
        recordingSectionUuids.some(
          (recordingUuid) =>
            !recordToSelectedSession?.[recordingUuid] && !is_empty(sessions)
        )
      ) {
        errorMessage = MANAGE_RECORDINGS_HOOK_TEXTS.SELECT_SESSION_ERROR;
      }

      if (
        showCustomerColumn &&
        recordingSectionUuids.some(
          (recordingUuid) => !recordToSelectedCustomer?.[recordingUuid]
        )
      ) {
        errorMessage = MANAGE_RECORDINGS_HOOK_TEXTS.SELECT_CUSTOMER_ERROR;
      }

      if (!is_empty(errorMessage)) {
        notify(errorMessage, notification_color_keys.error);
        return;
      }
      toggleShowSaveSectionChangesModal();
    } else {
      notify(
        MANAGE_RECORDINGS_HOOK_TEXTS.MAP_ATLEAST_ONE_ERROR,
        notification_color_keys.error
      );
    }
  };

  const handleViewRecordingActionClick = (record) => {
    setViewRecordingModal(record);
    handleToggleShowViewRecordingModal();
  };

  const handleSelectedSectionMappingChange = (record, value) => {
    setRecordToSelectedSection((prev) => ({
      ...prev,
      [record[column_keys.recordingId.apiKey]]: value,
    }));
  };

  const handleSelectedBatchMappingChange = (record, value) => {
    setRecordToSelectedBatch((prev) => ({
      ...prev,
      [record[column_keys.recordingId.apiKey]]: value,
    }));
  };

  const handleSelectedSessionMappingChange = (record, value) => {
    setRecordToSelectedSession((prev) => ({
      ...prev,
      [record[column_keys.recordingId.apiKey]]: value,
    }));
  };

  const handleSelectedCustomerMappingChange = (record, value) => {
    setRecordToSelectedCustomer((prev) => ({
      ...prev,
      [record[column_keys.recordingId.apiKey]]: value,
    }));
  };

  const handleClearMappingStates = () => {
    setRecordToSelectedSection({});
    setRecordToSelectedBatch({});
    setRecordToSelectedSession({});
    setRecordToSelectedCustomer({});
  };

  return {
    listingUuid,
    parentOffering,
    showViewRecordingModal,
    viewRecordingModalData,
    handleToggleShowViewRecordingModal,
    handleViewRecordingActionClick,
    recordToSelectedSection,
    handleSelectedSectionMappingChange,
    courseSectionOptions,
    setCourseSectionOptions,
    showSectionChangesModal,
    toggleShowSaveSectionChangesModal,
    handleSaveChnagesClick,
    zoomRcDetails,
    rcListingUuid,
    batchesProps: {
      showBatchesColumn: showBatchColumn,
      options: batches,
      recordToSelectedBatch,
      handleBatchChange: handleSelectedBatchMappingChange,
    },
    sessionsProps: {
      showSessionsColumn: showSessionColumn,
      options: sessions,
      recordToSelectedSession,
      handleSessionChange: handleSelectedSessionMappingChange,
    },
    customersProps: {
      showCustomersColumn: showCustomerColumn,
      options: customers,
      recordToSelectedCustomer,
      handleCustomerChange: handleSelectedCustomerMappingChange,
    },
    isPageDataLoaing,
    getZoomRcOfferingDetails,
    handleClearMappingStates,
  };
};

export default useManageRecordings;
